var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { ColorSchemeMenu } from '@a0/docs-components/ui/Header/ColorSchemeMenu';
import { HamburgerMenu } from '@a0/docs-components/ui/Header/HamburgerMenu';
import { AppBar, AppBarActions, AppBarContent, Box, ColumnLayout, styled, useTheme } from '@auth0/quantum-product';
import Collapse from '@mui/material/Collapse';
import Cookies from 'js-cookie';
import * as React from 'react';
import ApplicationStore from '../../stores/ApplicationStore';
import { SUPPORTED_LOCALES } from '../../util/locale';
import { useFluxible } from '../hooks/useFluxible';
import { SearchBox } from '../SearchBox';
import { ActionArea } from './ActionArea';
import { LanguageMenu } from './LanguageMenu';
import { Logo } from './Logo';
import { NavigationDrawer } from './NavigationDrawer';
import { NavLinks } from './NavLinks';
import OptInBanner from './OptInBanner';
var supportedLocales = SUPPORTED_LOCALES.map(function (locale) { return locale.toLowerCase(); });
var HeaderBar = styled(AppBar, {
    shouldForwardProp: function (prop) {
        return !['fixed', 'slideOut', 'isHomePage', 'mode', 'optInBanner'].includes(prop);
    },
})(function (_a) {
    var _b;
    var theme = _a.theme, fixed = _a.fixed, slideOut = _a.slideOut, mode = _a.mode, isHomePage = _a.isHomePage, optInBanner = _a.optInBanner;
    return (_b = {
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(0, 3),
            backgroundColor: fixed && isHomePage && mode !== 'dark' ? theme.palette.background.homePage : theme.tokens.color_bg_page,
            top: optInBanner ? '30px' : undefined,
            overflow: 'visible',
            position: fixed ? 'fixed' : 'relative'
        },
        _b[theme.breakpoints.down(theme.layout.tabletBreakpoint)] = {
            padding: 2,
        },
        _b);
});
var HeaderContent = styled(AppBarContent)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            maxWidth: theme.layout.header1440,
            width: 'inherit',
            padding: 1,
            overflow: 'hidden',
            borderBottom: "1px solid ".concat(theme.tokens.color_border_default)
        },
        _b[theme.breakpoints.down(theme.layout.tabletBreakpoint)] = {
            maxWidth: '100%',
            padding: theme.spacing(0, theme.layout.mobileMarginX),
            paddingBottom: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
        _b);
});
var ColumnStack = styled(ColumnLayout)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            gridTemplateColumns: 'none',
            gridAutoColumns: 'min-content',
            gridAutoFlow: 'column',
            alignItems: 'center'
        },
        _b[theme.breakpoints.up('md')] = {
            gridTemplateColumns: 'none',
        },
        _b);
});
export var Header = function (_a) {
    var currentSection = _a.currentSection, currentRouteName = _a.currentRouteName, sections = _a.sections, _b = _a.isAuthenticated, isAuthenticated = _b === void 0 ? false : _b, user = _a.user;
    var context = useFluxible();
    var appStore = context.getStore(ApplicationStore);
    var isDocsI18nEnabled = appStore.getIsDocsI18nEnabled();
    var i18nEnabledLanguages = appStore.getI18nEnabledLanguages();
    var mintlifyOptInBanner = appStore.getMintlifyOptInBanner();
    var isHomePage = currentRouteName.includes('home');
    var routeNames = ['home', 'homefrca', 'homejajp', 'quickstarts', 'article', 'library'];
    var isFixedHeader = routeNames.includes(currentRouteName);
    var _c = __read(React.useState(false), 2), showSearch = _c[0], setShowSearch = _c[1];
    var _d = __read(React.useState(undefined), 2), backgroundColor = _d[0], setBackgroundColor = _d[1];
    var _e = __read(React.useState(false), 2), expandMenu = _e[0], setExpandMenu = _e[1];
    var _f = __read(React.useState('/docs'), 2), hrefPath = _f[0], setHrefPath = _f[1];
    var _g = __read(React.useState(sections), 2), localizedSections = _g[0], setLocalizedSections = _g[1];
    var headerElRef = React.useRef(null);
    var theme = useTheme();
    var handleMenuToggle = function (force) {
        var shouldExpandMenu = typeof force === 'boolean' ? force : !expandMenu;
        setExpandMenu(shouldExpandMenu);
    };
    var getLocalizedSections = function (locale) {
        return sections.map(function (section) {
            var urlParts = section.url.split('/docs');
            var localizedUrl = locale === 'en-us' ? section.url : "".concat(urlParts[0], "/docs/").concat(locale).concat(urlParts[1]);
            return __assign(__assign({}, section), { url: localizedUrl });
        });
    };
    React.useEffect(function () {
        var handleScroll = function () {
            if (!headerElRef.current) {
                return;
            }
            var scrollTop = window.scrollY || 0;
            // @TODO - temp
            setBackgroundColor(scrollTop > 553 ? theme.tokens.color_bg_page : undefined);
            setShowSearch(scrollTop > headerElRef.current.offsetHeight);
        };
        if (isHomePage) {
            document.addEventListener('scroll', handleScroll);
            return function () {
                document.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isHomePage, theme.palette.mode]);
    React.useEffect(function () {
        // Retrieve the locale from local storage
        var storedLocale = localStorage.getItem('i18nextLng') || 'en-us';
        var locale = supportedLocales.includes(storedLocale) ? storedLocale : 'en-us';
        Cookies.set('USER_PREFERRED_LOCALE', locale);
        setHrefPath(locale === 'en-us' ? '/docs' : "/docs/".concat(storedLocale));
        setLocalizedSections(getLocalizedSections(locale));
    }, []);
    return (React.createElement(React.Fragment, null,
        mintlifyOptInBanner ? (React.createElement(React.Fragment, null,
            React.createElement(OptInBanner, null),
            React.createElement(Box, { sx: { height: '100px' } }),
            " ")) : null,
        React.createElement(HeaderBar, { mode: theme.palette.mode, slideOut: expandMenu, fixed: isFixedHeader, isHomePage: isHomePage, ref: function (el) {
                headerElRef.current = el;
            }, style: { backgroundColor: backgroundColor }, "data-search-index": "false", optInBanner: mintlifyOptInBanner },
            React.createElement(HeaderContent, null,
                React.createElement(AppBarActions, null,
                    React.createElement(Box, { sx: { marginRight: 3 } },
                        React.createElement(Logo, { href: hrefPath }))),
                React.createElement(AppBarActions, { sx: { flex: 1, justifyContent: 'space-between' } },
                    isHomePage ? (React.createElement(Collapse, { in: showSearch },
                        React.createElement(SearchBox, { placement: "header" }))) : (React.createElement(SearchBox, { placement: "header" })),
                    React.createElement(NavLinks, { sections: localizedSections, currentSection: currentSection, onClick: function () { return handleMenuToggle(false); } })),
                React.createElement(AppBarActions, null,
                    React.createElement(ActionArea, { placement: "header", isAuthenticated: isAuthenticated, user: user })),
                React.createElement(AppBarActions, null, isDocsI18nEnabled && React.createElement(LanguageMenu, { enabledLanguages: i18nEnabledLanguages })),
                React.createElement(AppBarActions, null,
                    React.createElement(ColorSchemeMenu, null)),
                React.createElement(HamburgerMenu, { expandMenu: expandMenu, onClick: function () { return handleMenuToggle(); } }))),
        React.createElement(NavigationDrawer, { isAuthenticated: isAuthenticated, user: user, show: expandMenu, onClose: function () {
                setExpandMenu(false);
            }, sections: sections, currentSection: currentSection })));
};
